import React, { useMemo, useRef, useState } from "react";
import { HomeOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Table, Input, Button, Space, Modal } from "antd";
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./browser_transaction_invoice.css";
import useSWR, { mutate } from "swr";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

interface TransactionDataType {
    key: number;
    idTransaksi: string;
    transactionDate: string;
    invoiceNo: string;
    transactionType: string;
    customerCode: string;
    customerName: string;
    amount: number;
    transactionTo: string;
    top: string;
    warehouse: string;
    remark: string;
}

interface ApiDataType {
    id: number;
    id_trans: string;
    date_trans: string;
    invoice_no: string;
    trans_type: string;
    customer_code: string;
    customer_name: string;
    amount: string;
    transaction_to: string;
    top: string;
    warehouse: string;
    remark: string;
}


type DataIndex = keyof TransactionDataType;
const backendUrl = process.env.REACT_APP_BACKEND_URL;
const fetcher = (url: string) => fetch(url).then(res => res.json());

const BrowserTransactionInvoice: React.FC = () => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };


    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [selectedRow, setSelectedRow] = useState<TransactionDataType | null>(null);
    const { userData } = useAuth();
    const { data: apiData, error, isLoading } = useSWR<ApiDataType[]>(`${backendUrl}/api/browser-transaction-invoices`, fetcher);

    const dataSource = useMemo(() => {
        if (!apiData) return [];
        return apiData.map(item => ({
            key: item.id,
            idTransaksi: item.id_trans,
            transactionDate: item.date_trans ? new Date(item.date_trans).toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-',
            invoiceNo: item.invoice_no,
            transactionType: item.trans_type === '1' ? 'Pembayaran Invoice' : 'Pembayaran Invoice DP',
            customerCode: item.customer_code,
            customerName: item.customer_name,
            amount: parseFloat(item.amount) || 0,
            transactionTo: item.transaction_to,
            top: item.top,
            remark: item.remark,
            warehouse: item.warehouse,

        }));
    }, [apiData]);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const handleDelete = async () => {
        if (!selectedRow) return;
        Modal.confirm({
            title: 'Konfirmasi Hapus',
            content: `Apakah Anda yakin ingin menghapus transaksi dengan ID: ${selectedRow.idTransaksi}?`,
            okText: 'Ya, Hapus',
            cancelText: 'Batal',
            onOk: async () => {
                try {
                    await axios.delete(`${backendUrl}/api/delete-transaction-invoice/${selectedRow.idTransaksi}`, {
                        data: { user_log: userData?.kode_grup1 }
                    });
                    Modal.success({
                        title: 'Berhasil',
                        content: 'Transaksi berhasil dihapus.',
                    });
                    setSelectedRow(null);
                    mutate(`${backendUrl}/api/browser-transaction-invoices`);
                } catch (error) {
                    console.error('Gagal menghapus transaksi:', error);
                    Modal.error({
                        title: 'Gagal',
                        content: 'Terjadi kesalahan saat menghapus transaksi.',
                    });
                }
            },
        });
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<TransactionDataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });


    const columns: TableColumnsType<TransactionDataType> = [
        {
            title: 'Id Transaksi',
            dataIndex: 'idTransaksi',
            key: 'idTransaksi',
            ...getColumnSearchProps('idTransaksi'),
            sorter: (a, b) => a.idTransaksi.localeCompare(b.idTransaksi),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Transaction Date',
            dataIndex: 'transactionDate',
            key: 'transactionDate',
            ...getColumnSearchProps('transactionDate'),
            sorter: (a, b) => new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime(),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Invoice No',
            dataIndex: 'invoiceNo',
            key: 'invoiceNo',
            ...getColumnSearchProps('invoiceNo'),
            sorter: (a, b) => a.invoiceNo.localeCompare(b.invoiceNo),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Transaction Type',
            dataIndex: 'transactionType',
            key: 'transactionType',
            ...getColumnSearchProps('transactionType'),
            sorter: (a, b) => a.transactionType.localeCompare(b.transactionType),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Customer Code',
            dataIndex: 'customerCode',
            key: 'customerCode',
            ...getColumnSearchProps('customerCode'),
            sorter: (a, b) => a.customerCode.localeCompare(b.customerCode),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Customer Name',
            dataIndex: 'customerName',
            key: 'customerName',
            ...getColumnSearchProps('customerName'),
            sorter: (a, b) => a.customerName.localeCompare(b.customerName),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            ...getColumnSearchProps('amount'),
            sorter: (a, b) => a.amount - b.amount,
            sortDirections: ['descend', 'ascend'],
            render: (text: number) => new Intl.NumberFormat('id-ID').format(text),
        },
        {
            title: 'Transaction To',
            dataIndex: 'transactionTo',
            key: 'transactionTo',
            ...getColumnSearchProps('transactionTo'),
            sorter: (a, b) => a.transactionTo.localeCompare(b.transactionTo),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'TOP',
            dataIndex: 'top',
            key: 'top',
            ...getColumnSearchProps('top'),
            sorter: (a, b) => a.top.localeCompare(b.top),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Remark',
            dataIndex: 'remark',
            key: 'remark',
            ...getColumnSearchProps('remark'),
            sorter: (a, b) => (a.remark || '').localeCompare(b.remark || ''),
            sortDirections: ['descend', 'ascend'],
        },
    ];

    return (

        <div id="ilqt" style={transformStyle}>
            <div className="gjs-grid-row" id="i7rz">
                <div className="gjs-grid-column" id="ii0o">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                title: (
                                    <>
                                        <span style={{ fontWeight: '200', color: 'rgba(0, 0, 0, 0.88)' }}>Browser Transaction Invoice</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/transaction_invoice",
                                title: "Transaction Invoice",
                            },
                        ]}
                    />
                </div>
            </div>

            <div className="gjs-grid-row" id="ivvrv">
                <div className="gjs-grid-column" id="ibu6">
                    <Button className="gr-button" onClick={handleDelete} disabled={!selectedRow}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ width: '1em', height: '1em', marginRight: '8px' }}>
                            <path
                                fillRule="evenodd"
                                d="M9 1.5H5.625c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5Zm6.61 10.936a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 14.47a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
                                clipRule="evenodd"
                            />
                            <path d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963Z" />
                        </svg>
                        Delete
                    </Button>
                </div>
            </div>

            <div className="gjs-grid-row" id="i9g78">
                <div className="gjs-grid-column" id="iiepq">
                    <Table<TransactionDataType>
                        columns={columns}
                        dataSource={dataSource}
                        loading={isLoading}
                        onRow={(record) => ({
                            onClick: () => {
                                setSelectedRow(record);
                            },
                            className: selectedRow?.key === record.key ? 'selected-row' : '',
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default BrowserTransactionInvoice;